import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { loginWithEmail, loginWithGoogle, sendOTPCode, verifyOTPCode } from '../services/api';
import ThemeToggle from '../components/ThemeToggle';
import AuthLayout from '../components/AuthLayout';
import { useToast } from '../components/ToastContainer';
import ToastContainer from '../components/ToastContainer';
import { t, getLanguage } from '../utils/i18n';
import { MdCheckCircle } from 'react-icons/md';

export default function Login() {
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [useOTP, setUseOTP] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [, setLang] = useState(getLanguage());
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, removeToast, toasts } = useToast();
  const redirectAfterLogin = location.state?.redirect;

  useEffect(() => {
    const handleLanguageChange = () => {
      setLang(getLanguage());
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await loginWithEmail(email);
      if (result.success) {
        setEmailSent(true);
        showToast(t('auth.login.checkEmail'), 'success', 6000);
      } else {
        setError(result.error || t('auth.login.linkError'));
        showToast(result.error || t('auth.login.linkError'), 'error', 6000);
      }
    } catch (error) {
      setError(error.message || t('auth.login.unknownError'));
      showToast(error.message || t('auth.login.unknownError'), 'error', 6000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!email) {
      showToast(t('auth.login.enterEmail'), 'error', 4000);
      return;
    }

    setIsOTPLoading(true);
    try {
      const result = await sendOTPCode(email);
      if (result.success) {
        setOtpSent(true);
        showToast(t('auth.login.otpSent'), 'success', 6000);
      } else {
        showToast(result.error || t('auth.login.otpError'), 'error', 6000);
      }
    } catch (error) {
      showToast(error.message || t('auth.login.otpError'), 'error', 6000);
    } finally {
      setIsOTPLoading(false);
    }
  };

  const handleSwitchToOTP = () => {
    setUseOTP(true);
    setError('');
    setEmailSent(false);
  };

  const handleSwitchToEmail = () => {
    setUseOTP(false);
    setError('');
    setEmailSent(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      showToast(t('auth.login.enterValidOTP'), 'error', 4000);
      return;
    }

    setIsOTPLoading(true);
    try {
      const result = await verifyOTPCode(email, otpCode);
      if (result.success) {
        showToast(t('auth.login.loginSuccess'), 'success', 2000);
        const destination = result.user?.profileComplete === false ? '/complete-profile' : (redirectAfterLogin || '/dashboard');
        setTimeout(() => {
          navigate(destination, { replace: true });
        }, 500);
      } else {
        showToast(result.error || t('auth.login.verifyError'), 'error', 6000);
      }
    } catch (error) {
      showToast(error.message || t('auth.login.verifyError'), 'error', 6000);
    } finally {
      setIsOTPLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    
    const isPlaceholder = !clientId || 
      clientId === 'YOUR_GOOGLE_CLIENT_ID' || 
      clientId.toLowerCase() === 'your-google-client-id' ||
      clientId.toLowerCase().includes('your-google') ||
      !clientId.includes('.apps.googleusercontent.com');
    
    if (isPlaceholder) {
      console.warn('Google Client ID not configured:', clientId);
      showToast(t('auth.login.googleNotConfigured'), 'info', 6000);
      return;
    }

    try {
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(() => reject(new Error('Google script load timeout')), 10000);
        });
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            const payload = JSON.parse(atob(response.credential.split('.')[1]));
            const idToken = response.credential;
            const email = payload.email;
            const displayName = payload.name;
            const photoUrl = payload.picture;

            const result = await loginWithGoogle(idToken, '', email, displayName, photoUrl);
            if (result.success) {
              showToast(t('auth.login.loginSuccess'), 'success', 2000);
              const destination = result.user?.profileComplete === false ? '/complete-profile' : (redirectAfterLogin || '/dashboard');
              setTimeout(() => {
                navigate(destination, { replace: true });
              }, 500);
            } else {
              showToast(result.error || t('auth.login.googleError'), 'error', 6000);
            }
          } catch (error) {
            showToast(t('auth.login.googleError'), 'error', 6000);
            console.error('Google Sign In callback error:', error);
          }
        },
      });

      const buttonContainer = document.createElement('div');
      buttonContainer.style.position = 'absolute';
      buttonContainer.style.left = '-9999px';
      buttonContainer.style.visibility = 'hidden';
      document.body.appendChild(buttonContainer);
      
      window.google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
      });
      
      setTimeout(() => {
        const googleButton = buttonContainer.querySelector('div[role="button"]');
        if (googleButton) {
          googleButton.click();
        } else {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
              const reason = notification.getNotDisplayedReason();
              if (import.meta.env.DEV) {
                console.log('Google Sign In not available:', reason);
              }
              if (reason === 'browser_not_supported' || reason === 'invalid_client') {
                showToast(t('auth.login.googleNotAvailable'), 'info', 6000);
              } else {
                showToast(t('auth.login.googleRequiresHTTPS'), 'info', 6000);
              }
            }
          });
        }
      }, 300);
    } catch (error) {
      showToast(t('auth.login.googleNotAvailable'), 'error', 6000);
      console.error('Google Sign In error:', error);
    }
  };

  return (
    <AuthLayout title="Prijava" subtitle="Prijavite se da biste zakazali čuvanje ili upravljali profilom">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      
      <div className="card p-8">
        {/* Header */}
        <div className="mb-5">
          <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-gray-100">Prijava</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Prijavite se da biste zakazali čuvanje ili upravljali profilom.
          </p>
        </div>

        {!otpSent ? (
          <>
            {/* Google Login - Primary */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 px-4 py-3.5 border-2 border-gray-300 dark:border-gray-600 rounded-[15px] hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium shadow-sm mb-5"
            >
              <img 
                src="https://www.google.com/favicon.ico" 
                alt="Google" 
                className="w-5 h-5"
              />
              <span>{t('auth.login.googleButton') || 'Nastavi sa Google'}</span>
            </button>

            <div className="relative mb-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">ili</span>
              </div>
            </div>

            {/* Email Form - Direct */}
            {!useOTP ? (
              <>
                {emailSent ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-[15px] flex items-start gap-3">
                      <MdCheckCircle className="text-green-600 dark:text-green-400 text-xl flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
                          Poslali smo link na email
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300">
                          Proverite inbox za {email}. Link je važeći 15 minuta.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setEmailSent(false);
                        handleSubmit({ preventDefault: () => {} });
                      }}
                      className="w-full text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors font-medium"
                    >
                      Pošalji ponovo
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">
                        {t('auth.login.emailLabel') || 'Email'}
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError('');
                        }}
                        required
                        className="input-base"
                        placeholder={t('auth.login.emailPlaceholder') || 'tvoj@email.com'}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1.5 text-slate-500">
                        Poslaćemo vam link za prijavu na email
                      </p>
                    </div>

                    {error && (
                      <div className="error-text text-sm">{error}</div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading || !email}
                      className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? t('auth.login.sending') || 'Šalje se...' : t('auth.login.sendLink') || 'Pošalji link'}
                    </button>
                  </form>
                )}
              </>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); handleSendOTP(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">
                    {t('auth.login.emailLabel') || 'Email'}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                    required
                    className="input-base"
                    placeholder={t('auth.login.emailPlaceholder') || 'tvoj@email.com'}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isOTPLoading || !email}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isOTPLoading ? t('auth.login.sendingOTP') : t('auth.login.sendOTP') || 'Pošalji kod'}
                </button>

                <button
                  type="button"
                  onClick={handleSwitchToEmail}
                  className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Prijava sa lozinkom
                </button>
              </form>
            )}

            {/* Alternative: OTP Login Link */}
            {!useOTP && !emailSent && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleSwitchToOTP}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Prijava bez lozinke (kod na email)
                </button>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">
                {t('auth.login.otpLabel') || 'Kod'}
              </label>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                className="w-full px-4 py-3 border rounded-[15px] focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white text-center text-2xl tracking-widest"
                placeholder={t('auth.login.otpPlaceholder') || '000000'}
              />
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1.5 text-center">
                {t('auth.login.otpSentTo') || 'Poslato na'} {email}
              </p>
            </div>

            <button
              type="submit"
              disabled={isOTPLoading || otpCode.length !== 6}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isOTPLoading ? t('auth.login.verifying') : t('auth.login.verifyOTP') || 'Potvrdi'}
            </button>

            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setOtpCode('');
              }}
              className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {t('auth.login.backToEmail') || 'Nazad na email'}
            </button>
          </form>
        )}

        <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Nemate nalog?{' '}
            <Link 
              to="/register" 
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors font-semibold"
            >
              Registrujte se
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
